/**
 * pdfGenerator.js — Shared PDF generation utility
 *
 * Uses jsPDF to create professional documents for quotes and invoices.
 * All templates share common header/footer formatting with company branding.
 *
 * Usage:
 *   import { generateQuotePdf, generateInvoicePdf } from './pdfGenerator';
 *   generateQuotePdf({ state, computed, activeTerms, settings });
 *   generateInvoicePdf({ invoice, job, settings });
 */

import { jsPDF } from 'jspdf';
import { paymentDate } from './utils';
import { splitDeliverables } from './jobDeliverables';

// ── Brand Kit (Apple-restrained, sourced from rotoflight-media DESIGN_SYSTEM.md) ──
// Palette intentionally mirrors the public website's design tokens so the
// printed quote feels like a continuation of the brand a client encountered
// on rotoflightmedia.com. Decisions:
//   - Apple off-white for page bg (warmer than pure white, premium feel)
//   - Apple ink for body (softer than pure black)
//   - Apple blue for accent (replaces the older cyan #13B5EA — that color
//     is digital/loud; Apple blue reads as restrained and trustworthy)
//   - Navy retained for typography weight (titles, headings)
//   - Gold reserved for premium tier only (not used yet — future opt-in)
const BRAND = {
  accent: [0, 113, 227],    // rgb(0,113,227) — Apple blue, primary CTAs / total
  navy:   [27, 42, 74],     // #1B2A4A — display type, headings
  ink:    [29, 29, 31],     // rgb(29,29,31) — Apple ink, body text
  muted:  [110, 110, 115],  // rgb(110,110,115) — Apple secondary text
  rule:   [223, 225, 229],  // #DFE1E5 — hairline dividers (unchanged)
  surface2: [251, 251, 253], // #fbfbfd — Apple off-white, panel fills + page bg
  surface3: [244, 245, 247], // #F4F5F7 — slightly deeper panel (was old surface2)
  green:  [46, 139, 87],    // #2E8B57 — discount / positive (unchanged)
  red:    [208, 74, 50],    // #D04A32 — overdue / negative (unchanged)
  gold:   [176, 137, 64],   // rgb(176,137,64) — premium tier only (reserved)
  white:  [255, 255, 255],
};

// Back-compat aliases — kept so any utility importing these by name still works.
// New code should reference BRAND.* directly.
const ACCENT = BRAND.accent;
const BLACK = BRAND.ink;
const GRAY = BRAND.muted;
const LIGHT_GRAY = BRAND.surface3; // semantic alias kept; points to deeper surface
const WHITE = BRAND.white;
const GREEN = BRAND.green;
const RED = BRAND.red;
const NAVY = BRAND.navy;
const RULE = BRAND.rule;

// Typography scale. Helvetica throughout (jsPDF built-in). Sizes in pt.
// Mirrors the website's display/body/caption ramp scaled down for print.
const TYPE = {
  hero: 28,     // hero band title: "Quote." — display
  title: 22,    // section-level title / total card amount
  section: 11,  // section headers ("Investment.", "Schedule.")
  meta: 9,      // metadata rows (date, quote#)
  body: 9,      // body copy (line items, panel content)
  small: 8,     // sub-body, captions
  micro: 7,     // labels, footers
  tiny: 6.5,    // legal-fine-print fallback
};

// Spacing scale — 8-multiples per Apple/website convention.
// Use these for vertical rhythm. In mm: 1mm ≈ 2.83px on screen.
// Picked the closest mm values to the website's px values so PDF and screen
// feel consonant when viewed side-by-side.
const SPACE = {
  xs: 1.5,   // 4px equiv
  sm: 3,     // 8px equiv
  md: 6,     // 16px equiv
  lg: 9,     // 24px equiv
  xl: 12,    // 32px equiv
  xxl: 18,   // 48px equiv
};

// Corner radius — website cards use 12px (standard) and 18px (tile-level).
// Scaled to print: 2.5mm ≈ 7px (subtle, premium) for body cards; 4mm for hero
// elements (matches the visual weight of a large website tile).
const RADIUS = {
  card: 2.5,
  hero: 4,
};

// Letter-spacing for display type — website uses -0.015em to -0.025em on
// hero headlines (tight Apple look). jsPDF's charSpace is in pt-absolute,
// not em-relative, so we tune empirically: -0.3pt on 22–28pt is the sweet spot.
const TRACKING_DISPLAY = -0.3;

// Layout
const MARGIN = 20;
const HEADER_BOTTOM = 28;
const FOOTER_TOP_FROM_BOTTOM = 20;

// Rule thickness standards (keeps dividers visually consistent)
const RULE_HAIR = 0.2;     // inter-row dividers, footer rule, section underlines
const RULE_SECTION = 0.4;  // totals divider, pre-total navy rule
const RULE_BRAND = 0.6;    // letterhead baseline

// ── Image loader ────────────────────────────────────────────────────────────
async function loadImageAsBase64(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── Formatters ──────────────────────────────────────────────────────────────
function fmtC(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

// Plain number, 2 decimals, no currency symbol (Xero convention — currency is
// named once in the "Amount USD" / "TOTAL USD" labels instead of per line).
function fmtN(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '0.00';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function fmtD(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  const d = new Date(dateOnly + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Long-form date including weekday — used in schedule day headings to give a
// concrete "Friday" anchor the client can sanity-check against their calendar.
function fmtDLong(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  const d = new Date(dateOnly + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// 24-hour "HH:MM" → 12-hour "H:MM AM/PM". Non-time values (e.g. "EOD") and
// already-formatted strings pass through unchanged.
function fmtT(timeStr) {
  if (!timeStr) return '';
  const s = String(timeStr).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  if (isNaN(h)) return s;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
}

// Short date with weekday, no year — "Thu, Jun 11" (compact timeline rows;
// the year is already shown in the quote header).
function fmtDShort(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  const d = new Date(dateOnly + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ── Shared drawing helpers ──────────────────────────────────────────────────

// Unified footer — navy brand line + subtle rule above
function drawFooter(doc, pageNum, totalPages, companyName, footerNote = '') {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, ph - FOOTER_TOP_FROM_BOTTOM, pw - MARGIN, ph - FOOTER_TOP_FROM_BOTTOM);
  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);
  doc.text(companyName || 'Roto Flight LLC', MARGIN, ph - 14);
  // Optional centered registered-office / legal line (Xero footer convention).
  if (footerNote) doc.text(footerNote, pw / 2, ph - 14, { align: 'center', maxWidth: pw - MARGIN * 2 - 60 });
  doc.text(`Page ${pageNum} of ${totalPages}`, pw - MARGIN, ph - 14, { align: 'right' });
}

// Unified letterhead — logo left, company block right, navy rule, title band
function drawLetterhead(doc, company, { title, subtitle, meta, hideContact = false, hideRule = false } = {}) {
  const { companyName, companyAddress, companyWeb, companyEmail, logoBase64 } = company;
  const pw = doc.internal.pageSize.getWidth();

  // Logo or wordmark (left)
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', MARGIN, 10, 0, 14);
    } catch {
      doc.setFontSize(18);
      doc.setTextColor(...BRAND.navy);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName || 'Roto Flight LLC', MARGIN, 22);
    }
  } else {
    doc.setFontSize(18);
    doc.setTextColor(...BRAND.navy);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName || 'Roto Flight LLC', MARGIN, 22);
  }

  // Company info (right) — suppressed on the quote page 1 (Xero puts the
  // registered-office line in the footer instead).
  if (!hideContact) {
    doc.setFontSize(TYPE.small);
    doc.setTextColor(...BRAND.muted);
    doc.setFont('helvetica', 'normal');
    let rightY = 13;
    if (companyAddress) { doc.text(companyAddress, pw - MARGIN, rightY, { align: 'right' }); rightY += 3.8; }
    if (companyWeb) { doc.text(companyWeb, pw - MARGIN, rightY, { align: 'right' }); rightY += 3.8; }
    if (companyEmail) { doc.text(companyEmail, pw - MARGIN, rightY, { align: 'right' }); rightY += 3.8; }
  }

  // Navy brand rule (suppressed for the clean Xero header)
  if (!hideRule) {
    doc.setDrawColor(...BRAND.navy);
    doc.setLineWidth(0.6);
    doc.line(MARGIN, HEADER_BOTTOM, pw - MARGIN, HEADER_BOTTOM);
  }

  let y = HEADER_BOTTOM + 6;

  // Title band — large navy title left, subtitle badge next to it; meta right-aligned
  if (title) {
    doc.setFontSize(TYPE.title);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.navy);
    doc.text(title, MARGIN, y + 6);

    if (subtitle) {
      const titleW = doc.getTextWidth(title);
      // Accent pill badge next to title
      doc.setFontSize(TYPE.micro);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND.white);
      doc.setFillColor(...BRAND.accent);
      const pillW = doc.getTextWidth(subtitle) + 6;
      doc.roundedRect(MARGIN + titleW + 4, y, pillW, 5, 1.2, 1.2, 'F');
      doc.text(subtitle, MARGIN + titleW + 4 + 3, y + 3.6);
    }

    // Meta rows (right-aligned)
    if (Array.isArray(meta) && meta.length) {
      doc.setFontSize(TYPE.meta);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BRAND.muted);
      let metaY = y + 2;
      meta.forEach((row) => {
        if (!row) return;
        doc.text(row, pw - MARGIN, metaY, { align: 'right' });
        metaY += 4.2;
      });
    }
    y += 12;
  }

  return y;
}

// Small-caps navy section header with a hairline rule.
// Used by the legacy invoice PDF + the rest of the quote PDF still being
// migrated to the new sentence-case pattern. Don't add new callers — use
// drawSentenceHeader for any new section.
function drawSectionHeader(doc, y, label) {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.text(String(label).toUpperCase(), MARGIN, y);
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  const labelW = doc.getTextWidth(String(label).toUpperCase());
  doc.line(MARGIN + labelW + 3, y - 1, pw - MARGIN, y - 1);
  return y + 5;
}

// Apple-restrained section header — sentence case, period, no underline.
// Translates the website's "Bold statement. Light context." headline pattern
// to print: a single bold navy word followed by a period, separated from
// content by whitespace instead of decorative rules.
//
// Example: "Investment.", "Schedule.", "Terms.", "Approval."
// Returns the new y after the header is drawn.
function drawSentenceHeader(doc, y, label) {
  doc.setFontSize(TYPE.section);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text(String(label), MARGIN, y);
  return y + SPACE.md;
}

// Page-1 content section band (Schedule / Delivery / Services / Acceptance).
// Establishes the top of the visual hierarchy: generous space above + a
// full-width hairline divider, then the sentence-case title. The divider and
// spacing separate the three body sections so a date/row heading beneath reads
// clearly as a child, not a sibling. Returns the content-start y.
function drawSectionBand(doc, y, label, { first = false } = {}) {
  const pw = doc.internal.pageSize.getWidth();
  y += first ? SPACE.md : SPACE.lg;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(RULE_HAIR);
  doc.line(MARGIN, y, pw - MARGIN, y);
  y += SPACE.sm + 2;
  doc.setFontSize(TYPE.section);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text(String(label), MARGIN, y);
  return y + SPACE.md;
}

// Quote header band (page 1) — Xero-style accounting-document header so the
// quote feels like a continuation of the client's Xero invoices.
//
// Layout (mirrors INV-0087):
//   Left:   "QUOTE" (large, black) + sender legal name beneath.
//   Middle: Date / Quote number (and Expires) as bold-label-over-value pairs.
//   Right:  the recipient (Quote-to) — name in caps + address lines.
// No rule, no total here (the total lives in the summary beneath the items).
//
// Returns the bottom-y, ready for the next section.
function drawQuoteHero(doc, y, { quoteNumber, quoteDate, validUntil, senderName, clientName, clientLines = [] }) {
  const pw = doc.internal.pageSize.getWidth();

  // Title (left) — uppercase, black, large.
  doc.setFontSize(TYPE.hero);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text('QUOTE', MARGIN, y + 9);
  // Sender legal name beneath the title.
  if (senderName) {
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.text(String(senderName), MARGIN, y + 14);
  }

  // Middle column — Date / Quote number / Expires (bold label over value).
  const pairs = [];
  if (quoteDate) pairs.push(['Date', fmtD(quoteDate)]);
  if (quoteNumber) pairs.push(['Quote number', quoteNumber]);
  if (validUntil) pairs.push(['Expires', fmtD(validUntil)]);
  const midX = MARGIN + 92;
  let mY = y + 3;
  pairs.forEach(([label, value]) => {
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.ink);
    doc.text(label, midX, mY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.text(String(value), midX, mY + 4);
    mY += 9.5;
  });

  // Right column — recipient block (name in caps + address lines), left-aligned
  // within the right region (Xero bill-to style).
  const rightX = pw - MARGIN - 56;
  let cY = y + 3;
  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  const nameLines = doc.splitTextToSize(String(clientName || 'Client').toUpperCase(), 56);
  nameLines.forEach((ln) => { doc.text(ln, rightX, cY); cY += 4.4; });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);
  clientLines.filter(Boolean).forEach((ln) => {
    doc.splitTextToSize(String(ln), 56).forEach((w) => { doc.text(w, rightX, cY); cY += 4; });
  });

  return Math.max(y + 18, mY, cY) + SPACE.md;
}

// "Prepared For" / "Bill To" panel — surface2 bg, label + name + contact lines.
// `flat: true` renders a Xero-style no-fill block (label, bold name, stacked
// muted contact lines, hairline beneath) instead of the gray panel.
function drawClientPanel(doc, y, { label, name, lines = [], rightLines = [], flat = false }) {
  const pw = doc.internal.pageSize.getWidth();
  const panelW = pw - MARGIN * 2;
  const colW = panelW / 2 - 8;

  if (flat) {
    const contact = [...lines, ...rightLines].filter(Boolean);
    // Label
    doc.setFontSize(TYPE.micro);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.muted);
    doc.text(String(label || 'Quote to'), MARGIN, y + 4);
    // Name
    doc.setFontSize(TYPE.section);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.ink);
    const nm = doc.splitTextToSize(String(name || 'Client'), panelW);
    doc.text(nm[0], MARGIN, y + 10);
    // Contact lines, stacked left
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    let cY = y + 14.5;
    contact.forEach((ln) => {
      doc.splitTextToSize(String(ln), panelW).forEach((w) => { doc.text(w, MARGIN, cY); cY += 4; });
    });
    // Hairline beneath the block
    const endY = cY + 1;
    doc.setDrawColor(...RULE);
    doc.setLineWidth(RULE_HAIR);
    doc.line(MARGIN, endY, pw - MARGIN, endY);
    return endY + SPACE.md;
  }

  // Pre-wrap contact lines at small body size so panel height fits content
  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'normal');
  const wrappedLeft = [];
  lines.forEach((ln) => {
    if (!ln) return;
    doc.splitTextToSize(String(ln), colW).forEach((w) => wrappedLeft.push(w));
  });
  const wrappedRight = [];
  rightLines.forEach((ln) => {
    if (!ln) return;
    doc.splitTextToSize(String(ln), colW).forEach((w) => wrappedRight.push(w));
  });
  const rows = Math.max(wrappedLeft.length, wrappedRight.length);
  const panelH = 12 + rows * 4;

  // surface3 = the original gray panel fill. Phase A swap kept identical so
  // there's zero visual change yet; Phase C will redesign this panel.
  doc.setFillColor(...BRAND.surface3);
  doc.rect(MARGIN, y, panelW, panelH, 'F');

  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.muted);
  // Render label as-passed (sentence case is the new convention). Callers
  // that want all-caps can opt in by passing an uppercased string.
  doc.text(String(label || 'Prepared for'), MARGIN + 4, y + 5);

  // Client name — wrap if extremely long, keep single line visually dominant
  doc.setFontSize(TYPE.section);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  const nameLines = doc.splitTextToSize(String(name || 'Client'), panelW - 8);
  doc.text(nameLines[0], MARGIN + 4, y + 10.5);

  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);

  let rowY = y + 15;
  wrappedLeft.forEach((ln) => { doc.text(ln, MARGIN + 4, rowY); rowY += 4; });
  let rRowY = y + 15;
  wrappedRight.forEach((ln) => { doc.text(ln, pw - MARGIN - 4, rRowY, { align: 'right' }); rRowY += 4; });

  return y + panelH + 6;
}

// "How to pay" block — card option + Zelle option, with card fee note
function drawHowToPay(doc, y, { payUrl, cardFeePct = 3, zelleAddress = 'contact@rotoflight.com' }) {
  const pw = doc.internal.pageSize.getWidth();
  y = drawSectionHeader(doc, y, 'How to pay');

  const hasLink = !!payUrl;
  const blockH = hasLink ? 22 : 16;
  // surface3 = the original gray panel fill — keep this invoice-side block
  // unchanged during the quote PDF redesign.
  doc.setFillColor(...BRAND.surface3);
  doc.rect(MARGIN, y, pw - MARGIN * 2, blockH, 'F');

  let rowY = y + 5;

  // Option 1: Card / Online
  doc.setFontSize(TYPE.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text('Pay online (card):', MARGIN + 4, rowY);

  if (hasLink) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.accent);
    const labelW = doc.getTextWidth('Pay online (card): ');
    const display = payUrl.length > 70 ? payUrl.substring(0, 67) + '…' : payUrl;
    doc.textWithLink(display, MARGIN + 4 + labelW, rowY, { url: payUrl });
    rowY += 4;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.text('link will be included with email', MARGIN + 4 + doc.getTextWidth('Pay online (card): '), rowY);
    rowY += 4;
  }

  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...BRAND.muted);
  doc.text(`A ${cardFeePct}% card processing fee applies.`, MARGIN + 4, rowY);
  rowY += 5;

  // Option 2: Zelle (no fee)
  doc.setFontSize(TYPE.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text('Prefer no fee? Pay via Zelle:', MARGIN + 4, rowY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.navy);
  const zLabelW = doc.getTextWidth('Prefer no fee? Pay via Zelle: ');
  doc.text(zelleAddress, MARGIN + 4 + zLabelW, rowY);

  return y + blockH + 6;
}

// Consolidate each service line into one timeline row: its shoot date(s) and
// its delivery date. Lets the quote show "this service — shot then, delivered
// then" without repeating the description in two separate Schedule / Delivery
// stacks, which also saves vertical space.
function buildServiceTimeline(lines = []) {
  return (lines || [])
    .map((line) => ({
      desc: line.desc || '',
      shoots: (Array.isArray(line.schedule) ? line.schedule : [])
        .filter((e) => e && e.date)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
        .map((e) => ({ date: e.date, time: e.time || '', endTime: e.endTime || '', location: e.location || '' })),
      deliveryDate: line.deliveryDate || '',
      deliveryTime: line.deliveryTime || 'EOD',
    }))
    .filter((s) => s.desc && (s.shoots.length > 0 || s.deliveryDate));
}

// Legacy wrappers — keep so nothing else breaks
function addFooter(doc, pageNum, totalPages, companyName) {
  return drawFooter(doc, pageNum, totalPages, companyName);
}
function addHeader(doc, company) {
  // Simple letterhead without title, returns body start Y
  return drawLetterhead(doc, company);
}

// ── QUOTE PDF ───────────────────────────────────────────────────────────────
// Options:
//   returnBlob: true  → return { blob, filename } without triggering download
//   default           → trigger browser download and return the filename string
export async function generateQuotePdf({ state, computed, activeTerms, settings, deposit = null, returnBlob = false }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const companyName = settings?.data?.companyName || settings?.companyName || 'Roto Flight LLC';
  const companyAddress = settings?.data?.companyAddress || settings?.companyAddress || '';
  const companyWeb = settings?.data?.companyWeb || settings?.companyWeb || '';
  const companyEmail = settings?.data?.companyEmail || settings?.companyEmail || '';
  const logoUrl = settings?.data?.logoUrl || settings?.logoUrl || '';
  const logoBase64 = await loadImageAsBase64(logoUrl);
  const company = { companyName, companyAddress, companyWeb, companyEmail, logoBase64 };

  const ensureSpace = (y, needed = 12) => {
    if (y + needed > ph - FOOTER_TOP_FROM_BOTTOM - 4) {
      doc.addPage();
      return drawLetterhead(doc, company);
    }
    return y;
  };

  // Clean Xero-style header — logo only (no company block / no navy rule); the
  // company's registered office goes in the footer instead. Continuation pages
  // still get the standard letterhead via ensureSpace.
  let y = drawLetterhead(doc, company, { hideContact: true, hideRule: true });

  // ── Header band (page 1) — title + date/number + quote-to recipient ──
  y = drawQuoteHero(doc, y, {
    quoteNumber: state.quoteNumber,
    quoteDate: state.quoteDate,
    validUntil: state.validUntil,
    senderName: companyName,
    clientName: state.client || 'Client',
    clientLines: [
      state.email || null,
      state.phone || null,
      state.address || null,
    ],
  });

  // ── Schedule & Delivery (one row per service: shoot left, delivery right) ──
  // Each service shows when it's shot and when it's delivered side by side, so
  // the two dates that belong to the same deliverable read together and the
  // description isn't repeated across separate Schedule / Delivery stacks.
  const timeline = buildServiceTimeline(state.lines || []);
  if (timeline.length > 0) {
    y = ensureSpace(y, 28);
    y = drawSectionBand(doc, y, 'Schedule & Delivery.', { first: true });

    const headX = MARGIN + SPACE.sm;
    const colX = MARGIN + SPACE.md;
    const splitX = MARGIN + (pw - MARGIN * 2) * 0.6; // shoot ~60% | delivery ~40%
    const shootW = splitX - colX - SPACE.sm;
    const delX = splitX + SPACE.sm;
    const delW = pw - MARGIN - delX;
    const rowH = 4.4;

    // Run-in column: bold label, then muted value wrapped with a hanging indent
    // under the value (label sits to its left). Returns lines drawn.
    const drawCol = (x, yy, colW, label, value) => {
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'bold');
      const labelW = doc.getTextWidth(`${label}  `);
      doc.setTextColor(...BRAND.ink);
      doc.text(label, x, yy);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BRAND.muted);
      const wrapped = doc.splitTextToSize(String(value || '—'), Math.max(24, colW - labelW));
      wrapped.forEach((w, i) => doc.text(w, x + labelW, yy + i * rowH));
      return wrapped.length;
    };

    timeline.forEach((svc) => {
      const shootStr = svc.shoots.length
        ? svc.shoots.map((e) => {
            const t = e.time ? (e.endTime ? `${fmtT(e.time)}–${fmtT(e.endTime)}` : fmtT(e.time)) : '';
            return [fmtDShort(e.date), t, e.location].filter(Boolean).join(' · ');
          }).join('   /   ')
        : '—';
      const delStr = svc.deliveryDate
        ? [fmtDShort(svc.deliveryDate), fmtT(svc.deliveryTime)].filter(Boolean).join(' · ')
        : '—';

      // Measure for an atomic block (heading + the taller of the two columns).
      doc.setFontSize(TYPE.body);
      doc.setFont('helvetica', 'bold');
      const headLines = doc.splitTextToSize(svc.desc, pw - MARGIN * 2 - SPACE.sm);
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'bold');
      const sLabelW = doc.getTextWidth('Shoot  ');
      const dLabelW = doc.getTextWidth('Delivery  ');
      const sLines = doc.splitTextToSize(shootStr, Math.max(24, shootW - sLabelW)).length;
      const dLines = doc.splitTextToSize(delStr, Math.max(24, delW - dLabelW)).length;
      const rowLines = Math.max(sLines, dLines);
      y = ensureSpace(y, headLines.length * 4.6 + rowLines * rowH + SPACE.md);

      // Service name (L2 heading)
      doc.setFontSize(TYPE.body);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND.ink);
      headLines.forEach((ln) => { doc.text(ln, headX, y); y += 4.6; });

      // Shoot (left) + Delivery (right), sharing one baseline.
      const rowY = y + 0.5;
      drawCol(colX, rowY, shootW, 'Shoot', shootStr);
      drawCol(delX, rowY, delW, 'Delivery', delStr);
      y = rowY + rowLines * rowH + SPACE.sm;
    });
  }

  // ── Services (Xero-style line-item table) ──
  y = ensureSpace(y, 24);
  y = drawSectionBand(doc, y, 'Services.');

  // Numeric columns are right-aligned (Xero convention). Right-edge X per column.
  const amountColX = pw - MARGIN;
  const unitColX = amountColX - 30;
  const qtyColX = unitColX - 20;
  const descColW = qtyColX - MARGIN - 18;

  // Header row — muted micro labels, navy hairline beneath.
  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text('Description', MARGIN, y);
  doc.text('Quantity', qtyColX, y, { align: 'right' });
  doc.text('Unit Price', unitColX, y, { align: 'right' });
  doc.text('Amount USD', amountColX, y, { align: 'right' });
  y += 2;
  doc.setDrawColor(...BRAND.ink);
  doc.setLineWidth(RULE_HAIR);
  doc.line(MARGIN, y, pw - MARGIN, y);
  y += SPACE.sm;

  const lines = state.lines || [];
  lines.forEach((line, i) => {
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    const dLines = doc.splitTextToSize(String(line.desc || ''), descColW);

    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'italic');
    const detailLines = line.detail ? doc.splitTextToSize(String(line.detail), descColW) : [];

    const descBlockH = dLines.length * 4.2;
    const detailBlockH = detailLines.length ? detailLines.length * 3.5 + 1 : 0;
    const rowH = descBlockH + detailBlockH + SPACE.md;
    y = ensureSpace(y, rowH + 2);

    // Faint hairline separator between rows.
    if (i > 0) {
      doc.setDrawColor(...RULE);
      doc.setLineWidth(RULE_HAIR);
      doc.line(MARGIN, y - SPACE.sm, pw - MARGIN, y - SPACE.sm);
    }

    // Description (ink, normal weight — Xero rows aren't bold)
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.ink);
    let descY = y;
    dLines.forEach((ln) => { descY += 4.2; doc.text(ln, MARGIN, descY); });

    // Detail (muted, italic) below description
    if (detailLines.length) {
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...BRAND.muted);
      let detY = descY + 1.5;
      detailLines.forEach((ln) => { detY += 3.5; doc.text(ln, MARGIN, detY); });
    }

    // Numeric columns — qty, unit price, amount aligned to the first desc baseline.
    const qty = parseFloat(line.qty) || 1;
    const rate = parseFloat(line.rate) || 0;
    const amount = qty * rate;
    const baseY = y + 4.2;
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.ink);
    doc.text(qty.toFixed(2), qtyColX, baseY, { align: 'right' });
    doc.text(fmtN(rate), unitColX, baseY, { align: 'right' });
    doc.text(fmtN(amount), amountColX, baseY, { align: 'right' });

    y += rowH;
  });

  // ── Totals (Xero-style, aligned to the item columns, black, no $) ──
  y += SPACE.sm;
  const hasDiscount = computed.discountAmount > 0;
  // Reserve extra room when the deposit/balance lines will render below TOTAL,
  // so they can't orphan over the footer.
  const depReserveMm = (parseFloat(deposit?.amount) || 0) > 0 ? 14 : 0;
  y = ensureSpace(y, 24 + depReserveMm);

  // Subtotal — label right-aligned in the Unit Price column, value in Amount.
  doc.setFontSize(TYPE.body);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.ink);
  doc.text('Subtotal', unitColX, y, { align: 'right' });
  doc.text(fmtN(computed.subtotal), amountColX, y, { align: 'right' });
  y += 6;

  // Discount with reason inline
  if (hasDiscount) {
    const reason = state.discountReason ? `Discount — ${state.discountReason}` : 'Discount';
    let labelText = reason;
    const maxLabelW = 60;
    while (doc.getTextWidth(labelText) > maxLabelW && labelText.length > 12) {
      labelText = labelText.slice(0, -2);
    }
    if (labelText !== reason) labelText = labelText.replace(/\.{0,3}$/, '…');
    doc.text(labelText, unitColX, y, { align: 'right' });
    doc.text(`-${fmtN(computed.discountAmount)}`, amountColX, y, { align: 'right' });
    y += 6;
  }

  // Rule above the total (right block only)
  doc.setDrawColor(...BRAND.ink);
  doc.setLineWidth(RULE_SECTION);
  doc.line(unitColX - 40, y - 2, amountColX, y - 2);
  y += 2;

  // TOTAL USD — bold black (Xero)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.ink);
  doc.text('TOTAL USD', unitColX, y + 2, { align: 'right' });
  doc.text(fmtN(computed.total), amountColX, y + 2, { align: 'right' });
  y += 8;

  // Deposit schedule — shown when a deposit invoice is being sent with the
  // quote: what's due now vs. on delivery, aligned to the totals columns.
  const depAmt = parseFloat(deposit?.amount) || 0;
  if (depAmt > 0) {
    const balance = Math.max(0, (computed.total || 0) - depAmt);
    // Prefer the intended pct passed in (avoids reverse-deriving from the PDF's
    // recomputed total, which can drift); fall back to the ratio. One decimal
    // only when it isn't a whole number (so 50% stays "50%", 33.33% → "33.3%").
    const ratio = Number.isFinite(deposit?.pct)
      ? deposit.pct
      : (computed.total > 0 ? (depAmt / computed.total) * 100 : 0);
    const pctLabel = ratio > 0
      ? (Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(1).replace(/\.0$/, ''))
      : '';
    y += SPACE.xs;
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.text(`Deposit due on acceptance${pctLabel ? ` (${pctLabel}%)` : ''}`, unitColX, y, { align: 'right' });
    doc.setTextColor(...BRAND.ink);
    doc.text(fmtN(depAmt), amountColX, y, { align: 'right' });
    y += 5;
    doc.setTextColor(...BRAND.muted);
    doc.text('Balance due on delivery', unitColX, y, { align: 'right' });
    doc.setTextColor(...BRAND.ink);
    doc.text(fmtN(balance), amountColX, y, { align: 'right' });
    y += SPACE.md;
  }

  // Duration hint (quiet metadata under the summary)
  if (computed.estimatedDuration > 0) {
    doc.setFontSize(TYPE.micro);
    doc.setTextColor(...BRAND.muted);
    doc.setFont('helvetica', 'italic');
    const hrs = Math.floor(computed.estimatedDuration / 60);
    const mins = computed.estimatedDuration % 60;
    doc.text(`Estimated duration: ${hrs}h ${mins}m`, pw - MARGIN, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += SPACE.md;
  }

  // ── Notes (client-facing "Quote Notes" — honors the line breaks typed) ──
  // Distinct from the term clauses below and from internalNotes (never on PDF).
  const noteText = (state.notes || '').trim();
  if (noteText) {
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    const noteLines = [];
    noteText.split('\n').forEach((para) => {
      const t = para.trim();
      if (!t) { noteLines.push(''); return; } // preserve intentional blank lines
      doc.splitTextToSize(t, pw - MARGIN * 2).forEach((ln) => noteLines.push(ln));
    });
    // Keep the Notes block together: if it won't fully fit in the space left on
    // this page, start it on a fresh one so the header never orphans from its
    // body. (Notes longer than a whole page still begin fresh, then spill via
    // the per-line guard below.) drawSectionBand consumes ~20mm (lead+rule+text).
    const blockH = 20
      + noteLines.reduce((h, ln) => h + (ln === '' ? 2.6 : 4.4), 0)
      + SPACE.sm;
    if (y + blockH > ph - FOOTER_TOP_FROM_BOTTOM - 4) {
      doc.addPage();
      y = drawLetterhead(doc, company);
    }
    y = drawSectionBand(doc, y, 'Notes.');
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    noteLines.forEach((ln) => {
      if (ln === '') { y += 2.6; return; }
      y = ensureSpace(y, 6); // long notes flow to a new page instead of overprinting
      doc.text(ln, MARGIN, y);
      y += 4.4;
    });
    y += SPACE.sm;
  }

  const enabledTerms = (activeTerms || []).filter((t) => t.on);

  // ── Acceptance (flat, Xero-style — closes the main content) ──
  // No signature fields: paying the deposit is the acceptance. Keeping this
  // block short also stops it from spilling onto its own page.
  const preparedBy = state.preparedBy || settings?.data?.preparedByDefault || settings?.preparedByDefault || '';
  y = ensureSpace(y, 40);
  y = drawSectionBand(doc, y, 'Acceptance.');

  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);
  const acceptNote = enabledTerms.length > 0
    ? 'Paying the deposit automatically confirms acceptance of this quote — its scope and pricing — and of the Terms & Conditions that follow.'
    : 'Paying the deposit automatically confirms acceptance of this quote — its scope and pricing.';
  doc.splitTextToSize(acceptNote, pw - MARGIN * 2).forEach((ln) => { doc.text(ln, MARGIN, y); y += 4.4; });
  y += SPACE.sm;

  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND.muted);
  doc.text(`Prepared by ${preparedBy || companyName}`, MARGIN, y);

  // ── Terms & Conditions — dedicated page(s), two-column auto-fit ──
  // All enabled clauses render on their own trailing page in two columns. The
  // font auto-shrinks (8 → 6.5pt floor) to fit a single page where possible;
  // if the clause volume still exceeds one page at the floor, it flows to a
  // second terms page with a "(continued)" header. Clauses stay atomic — a
  // clause that won't fit the current column moves whole to the next column /
  // page. The internal `note` field is intentionally never rendered here.
  if (enabledTerms.length > 0) {
    const colGap = SPACE.md;
    const colW = (pw - MARGIN * 2 - colGap) / 2;
    const colX = [MARGIN, MARGIN + colW + colGap];
    // Fixed content top for terms pages (after letterhead + section header),
    // used by both the measure pass and the render pass so they agree.
    const topY = HEADER_BOTTOM + 6 + SPACE.md + SPACE.sm;
    const bottomY = ph - FOOTER_TOP_FROM_BOTTOM - 4;

    const items = enabledTerms.map((t, i) => ({
      prefix: `${i + 1}. ${String(t.label || 'Term')}.`,
      words: String(t.body || '').split(/\s+/).filter(Boolean),
    }));

    // Lay out one clause within a column at a given font size: returns the bold
    // prefix, the body words that fit on line 1 after it, and the wrapped rest.
    const layoutItem = (item, fs) => {
      doc.setFontSize(fs);
      doc.setFont('helvetica', 'bold');
      const prefixW = doc.getTextWidth(item.prefix);
      doc.setFont('helvetica', 'normal');
      const spaceW = doc.getTextWidth(' ');
      const gap = spaceW * 2; // breathing room after the bold "N. Label."
      const firstMaxW = colW - prefixW - gap;
      const firstWords = [];
      let fw = 0, wi = 0;
      while (wi < item.words.length) {
        const ww = doc.getTextWidth(item.words[wi]);
        const need = fw + (firstWords.length ? spaceW : 0) + ww;
        if (need > firstMaxW) break;
        firstWords.push(item.words[wi]); fw = need; wi++;
      }
      const firstBody = firstWords.join(' ');
      const remaining = item.words.slice(wi).join(' ');
      const restLines = remaining ? doc.splitTextToSize(remaining, colW) : [];
      return { prefix: item.prefix, prefixW, gap, firstBody, restLines };
    };

    // Pages needed at a given font size (clause-atomic column→column→page flow).
    const measurePages = (fs) => {
      const lineH = fs * 0.42;
      let pages = 1, col = 0, yc = topY;
      for (const item of items) {
        const h = (1 + layoutItem(item, fs).restLines.length) * lineH + SPACE.xs;
        if (yc + h > bottomY) {
          col++;
          if (col > 1) { col = 0; pages++; }
          yc = topY;
        }
        yc += h;
      }
      return pages;
    };

    // Largest font that fits one page; otherwise the 6.5pt floor.
    const FONTS = [TYPE.small, 7.5, 7, TYPE.tiny];
    let fs = FONTS[FONTS.length - 1];
    for (const cand of FONTS) {
      if (measurePages(cand) === 1) { fs = cand; break; }
    }
    const lineH = fs * 0.42;

    const startTermsPage = (continued) => {
      doc.addPage();
      let yy = drawLetterhead(doc, company);
      drawSentenceHeader(doc, yy, continued ? 'Terms & Conditions (continued).' : 'Terms & Conditions.');
    };

    startTermsPage(false);
    let col = 0;
    let yc = topY;
    items.forEach((item) => {
      const L = layoutItem(item, fs);
      const h = (1 + L.restLines.length) * lineH + SPACE.xs;
      if (yc + h > bottomY) {
        col++;
        if (col > 1) { col = 0; startTermsPage(true); }
        yc = topY;
      }
      const x = colX[col];
      // Line 1: bold prefix (ink) + body remainder (muted) on the same baseline.
      doc.setFontSize(fs);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND.ink);
      doc.text(L.prefix, x, yc);
      if (L.firstBody) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BRAND.muted);
        doc.text(L.firstBody, x + L.prefixW + L.gap, yc);
      }
      yc += lineH;
      // Wrapped continuation, flush to the column edge.
      if (L.restLines.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...BRAND.muted);
        L.restLines.forEach((ln) => {
          doc.text(ln, x, yc);
          yc += lineH;
        });
      }
      yc += SPACE.xs;
    });
  }

  // ── Footer on all pages ──
  const footerNote = companyAddress ? `Registered office: ${companyAddress}` : '';
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, companyName, footerNote);
  }

  const clientName = (state.client || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `Quote_${state.quoteNumber || 'Draft'}_${clientName}.pdf`;
  if (returnBlob) {
    const blob = doc.output('blob');
    return { blob, filename };
  }
  doc.save(filename);
  return filename;
}

// ── INVOICE PDF ─────────────────────────────────────────────────────────────
// Options:
//   returnBlob: true  → return { blob, filename } without triggering download
//   default           → trigger browser download and return the filename string
export async function generateInvoicePdf({ invoice, job, settings, returnBlob = false }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const companyName = settings?.data?.companyName || settings?.companyName || 'Roto Flight LLC';
  const companyAddress = settings?.data?.companyAddress || settings?.companyAddress || '';
  const companyWeb = settings?.data?.companyWeb || settings?.companyWeb || '';
  const companyEmail = settings?.data?.companyEmail || settings?.companyEmail || '';
  const logoUrl = settings?.data?.logoUrl || settings?.logoUrl || '';
  const logoBase64 = await loadImageAsBase64(logoUrl);
  const company = { companyName, companyAddress, companyWeb, companyEmail, logoBase64 };

  // Payment config (fallbacks so we don't need Settings UI work in this pass)
  const cardFeePct = Number(settings?.data?.paymentCardFeePct ?? settings?.paymentCardFeePct ?? 3);
  const zelleAddress = settings?.data?.paymentZelleAddress || settings?.paymentZelleAddress || 'contact@rotoflight.com';
  const fallbackPaymentLink = settings?.data?.paymentLink || settings?.paymentLink || '';
  const payUrl = invoice.xeroOnlineUrl || fallbackPaymentLink || '';

  const ensureSpace = (y, needed = 12) => {
    if (y + needed > ph - FOOTER_TOP_FROM_BOTTOM - 4) {
      doc.addPage();
      return drawLetterhead(doc, company);
    }
    return y;
  };

  const subtypeLabel = invoice.invoiceType === 'deposit' ? 'DEPOSIT'
    : invoice.invoiceType === 'balance' ? 'BALANCE' : null;

  const meta = [
    invoice.number ? `Invoice # ${invoice.number}` : null,
    invoice.createdAt ? `Issued: ${fmtD(invoice.createdAt)}` : null,
    invoice.dueDate ? `Due: ${fmtD(invoice.dueDate)}` : null,
  ].filter(Boolean);

  let y = drawLetterhead(doc, company, { title: 'INVOICE', subtitle: subtypeLabel, meta });

  // ── Status pill (below title, left-aligned) ──
  const rawStatus = (invoice.status || 'draft').toLowerCase();
  const statusUpper = rawStatus.toUpperCase();
  const statusColor = rawStatus === 'paid' ? BRAND.green
    : rawStatus === 'overdue' ? BRAND.red
    : rawStatus === 'partial' ? BRAND.accent
    : rawStatus === 'void' ? BRAND.muted
    : BRAND.navy;
  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.setFillColor(...statusColor);
  const pillW = doc.getTextWidth(statusUpper) + 6;
  doc.roundedRect(MARGIN, y, pillW, 5, 1.2, 1.2, 'F');
  doc.text(statusUpper, MARGIN + 3, y + 3.6);
  doc.setFont('helvetica', 'normal');
  y += 9;

  // Bulk (multi-job) invoices snapshot their per-property breakdown in
  // lineItems — when present it is the source of truth for the rows below,
  // regardless of whether a job object was passed.
  const bulkItems = Array.isArray(invoice.lineItems) && invoice.lineItems.length
    ? invoice.lineItems
    : null;

  // ── Bill to panel ──
  y = drawClientPanel(doc, y, {
    label: 'Bill to',
    name: invoice.client || 'Client',
    lines: [
      invoice.clientEmail || null,
      invoice.clientPhone || null,
    ],
    rightLines: bulkItems ? [
      `${bulkItems.length} ${bulkItems.length === 1 ? 'property' : 'properties'}`,
    ] : [
      job?.title ? `Job: ${job.title}` : null,
      job?.location || null,
    ],
  });

  // ── Balance Due hero card ──
  const amt = parseFloat(invoice.amount) || 0;
  const paidAmt = parseFloat(invoice.paidAmount) || 0;
  const balance = amt - paidAmt;
  const heroLabel = rawStatus === 'paid' ? 'PAID IN FULL'
    : (invoice.invoiceType === 'deposit' ? 'DEPOSIT DUE'
      : invoice.invoiceType === 'balance' ? 'BALANCE DUE'
      : 'AMOUNT DUE');
  const heroValue = rawStatus === 'paid' ? fmtC(amt) : fmtC(balance > 0 ? balance : amt);

  y = ensureSpace(y, 24);
  doc.setFillColor(...BRAND.navy);
  doc.rect(MARGIN, y, pw - MARGIN * 2, 20, 'F');
  doc.setFontSize(TYPE.micro);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.text(heroLabel, MARGIN + 6, y + 7);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(heroValue, pw - MARGIN - 6, y + 14, { align: 'right' });
  if (invoice.dueDate && rawStatus !== 'paid') {
    doc.setFontSize(TYPE.micro);
    doc.setFont('helvetica', 'normal');
    doc.text(`Due ${fmtD(invoice.dueDate)}`, MARGIN + 6, y + 14);
  }
  doc.setFont('helvetica', 'normal');
  y += 26;

  // ── Line items ──
  // Add-ons live outside deliverableLines (sid invariant) but are billed, so
  // the invoice must list them or the rows will not account for the total.
  const calcLines = bulkItems ? [] : [
    ...(job?.calcSnapshot?.deliverableLines || []),
    ...(job?.calcSnapshot?.addOnLines || []),
  ];
  const deliverableLines = job?.deliverableLines || job?.deliverables || [];
  const hasDetailedLines = calcLines.length > 0;

  y = ensureSpace(y, 14);
  y = drawSectionHeader(doc, y, 'Line Items');

  // Navy header row
  doc.setFontSize(TYPE.small);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.white);
  doc.setFillColor(...BRAND.navy);
  doc.rect(MARGIN, y, pw - MARGIN * 2, 6, 'F');
  doc.text('Description', MARGIN + 4, y + 4);
  if (hasDetailedLines) {
    doc.text('Qty', pw - 68, y + 4, { align: 'right' });
    doc.text('Rate', pw - 46, y + 4, { align: 'right' });
  }
  doc.text('Amount', pw - MARGIN - 4, y + 4, { align: 'right' });
  y += 6;

  doc.setFont('helvetica', 'normal');

  if (bulkItems) {
    // Per-property sections: bold address heading with the property subtotal,
    // service rows beneath. Amounts only — no Rate column (on VirTour jobs
    // line.rate is retail while amount is our share; printing both would
    // contradict the totals).
    const bulkDescColW = pw - MARGIN * 2 - 40;
    bulkItems.forEach((li, i) => {
      const label = li.location || li.title || 'Property';
      const services = (li.services || []).filter((s) => s && s.desc);
      doc.setFontSize(TYPE.small);
      const labelLines = doc.splitTextToSize(String(label), bulkDescColW);
      const rowH = 4 + labelLines.length * 4 + services.length * 3.5 + (services.length ? 1.5 : 0);
      y = ensureSpace(y, rowH + 2);
      if (i > 0) {
        doc.setDrawColor(...RULE);
        doc.setLineWidth(RULE_HAIR);
        doc.line(MARGIN, y, pw - MARGIN, y);
      }
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND.ink);
      let rowY = y + 4;
      labelLines.forEach((ln) => { doc.text(ln, MARGIN + 4, rowY); rowY += 4; });
      doc.text(fmtC(li.amount), pw - MARGIN - 4, y + 4, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      if (services.length) {
        doc.setFontSize(TYPE.tiny);
        let svcY = rowY + 0.5;
        services.forEach((svc) => {
          doc.setTextColor(...BRAND.muted);
          doc.text(`  ${String(svc.desc)}`, MARGIN + 4, svcY);
          if (svc.amount !== undefined && svc.amount !== null) {
            doc.text(fmtC(svc.amount), pw - MARGIN - 4, svcY, { align: 'right' });
          }
          svcY += 3.5;
        });
        doc.setTextColor(...BRAND.ink);
      }
      y += rowH;
    });
  } else if (hasDetailedLines) {
    const invDescColW = pw - MARGIN - 76;
    calcLines.forEach((line, i) => {
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(String(line.desc || 'Service'), invDescColW);
      doc.setFontSize(TYPE.tiny);
      const detailLines = line.detail ? doc.splitTextToSize(String(line.detail), invDescColW) : [];
      const rowH = 4 + descLines.length * 4 + (detailLines.length ? detailLines.length * 3 + 1 : 0);
      y = ensureSpace(y, rowH + 2);
      if (i > 0) {
        doc.setDrawColor(...RULE);
        doc.setLineWidth(RULE_HAIR);
        doc.line(MARGIN, y, pw - MARGIN, y);
      }
      doc.setFontSize(TYPE.small);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...BRAND.ink);
      let descY = y + 4;
      descLines.forEach((ln) => { doc.text(ln, MARGIN + 4, descY); descY += 4; });
      if (detailLines.length) {
        doc.setFontSize(TYPE.tiny);
        doc.setTextColor(...BRAND.muted);
        let detY = descY + 0.5;
        detailLines.forEach((ln) => { doc.text(ln, MARGIN + 4, detY); detY += 3; });
      }
      doc.setFontSize(TYPE.small);
      doc.setTextColor(...BRAND.ink);
      doc.setFont('helvetica', 'normal');
      doc.text(String(line.qty || 1), pw - 68, y + 4, { align: 'right' });
      doc.text(fmtC(line.rate), pw - 46, y + 4, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(fmtC(line.amount || (parseFloat(line.qty) || 1) * (parseFloat(line.rate) || 0)), pw - MARGIN - 4, y + 4, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      y += rowH;
    });
  } else {
    // Summary line
    const typeDesc = invoice.invoiceType === 'deposit'
      ? `Deposit for: ${job?.title || 'Job'}`
      : invoice.invoiceType === 'balance'
        ? `Balance for: ${job?.title || 'Job'}`
        : job?.title || 'Services rendered';
    y = ensureSpace(y, 8);
    doc.setFontSize(TYPE.small);
    doc.setTextColor(...BRAND.ink);
    doc.text(String(typeDesc).substring(0, 68), MARGIN + 4, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.text(fmtC(amt), pw - MARGIN - 4, y + 4, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += 7;

    if (deliverableLines && deliverableLines.length) {
      doc.setFontSize(TYPE.micro);
      doc.setTextColor(...BRAND.muted);
      const delLines = Array.isArray(deliverableLines)
        ? deliverableLines.map((dl) => typeof dl === 'object' ? (dl.desc || dl.name || 'Item') : String(dl))
        : splitDeliverables(deliverableLines);
      delLines.forEach((dl) => {
        y = ensureSpace(y, 4);
        doc.text(`  •  ${String(dl).trim()}`, MARGIN + 4, y + 3);
        y += 3.5;
      });
    }
  }

  // Closing rule under last row
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, pw - MARGIN, y);

  // ── Totals stack ──
  y += 5;
  y = ensureSpace(y, 28);
  const discAmt = parseFloat(job?.discountAmt ?? job?.discount) || 0;
  const discPct = parseFloat(job?.discountPct) || 0;

  doc.setFontSize(TYPE.body);
  doc.setFont('helvetica', 'normal');

  // The line items describe the whole project, but a deposit / balance invoice
  // bills only part of it — so the rows legitimately do not sum to Total. When
  // that happens, print the project total first and name the billed portion, so
  // the drop reads as intentional rather than as an arithmetic error.
  const rowsSum = hasDetailedLines
    ? calcLines.reduce((s, l) => s + (parseFloat(l.amount) || (parseFloat(l.qty) || 1) * (parseFloat(l.rate) || 0)), 0)
    : 0;
  const partialBill = hasDetailedLines && Math.abs(rowsSum - discAmt - amt) > 0.005;

  if (discAmt > 0 && hasDetailedLines) {
    const subtotal = partialBill ? rowsSum : amt + discAmt;
    doc.setTextColor(...BRAND.muted);
    doc.text('Subtotal', pw - 80, y);
    doc.setTextColor(...BRAND.ink);
    doc.text(fmtC(subtotal), pw - MARGIN - 4, y, { align: 'right' });
    y += 5;
    doc.setTextColor(...BRAND.green);
    doc.text(discPct > 0 ? `Discount (${discPct}%)` : 'Discount', pw - 80, y);
    doc.text(`-${fmtC(discAmt)}`, pw - MARGIN - 4, y, { align: 'right' });
    y += 5;
  }

  if (partialBill) {
    doc.setTextColor(...BRAND.muted);
    doc.text('Project total', pw - 80, y);
    doc.setTextColor(...BRAND.ink);
    doc.text(fmtC(rowsSum - discAmt), pw - MARGIN - 4, y, { align: 'right' });
    y += 5;
  }

  doc.setTextColor(...BRAND.muted);
  doc.text(
    partialBill
      ? (invoice.invoiceType === 'deposit' ? 'Deposit portion'
        : invoice.invoiceType === 'balance' ? 'Balance portion'
        : 'Amount billed')
      : 'Total',
    pw - 80,
    y,
  );
  doc.setTextColor(...BRAND.ink);
  doc.text(fmtC(amt), pw - MARGIN - 4, y, { align: 'right' });
  y += 5;

  if (paidAmt > 0) {
    doc.setTextColor(...BRAND.green);
    doc.text('Paid', pw - 80, y);
    doc.text(`-${fmtC(paidAmt)}`, pw - MARGIN - 4, y, { align: 'right' });
    y += 5;
  }

  doc.setDrawColor(...BRAND.navy);
  doc.setLineWidth(0.4);
  doc.line(pw - 80, y, pw - MARGIN, y);
  y += 6;

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.navy);
  doc.text('BALANCE DUE', pw - 80, y);
  doc.setTextColor(...BRAND.accent);
  doc.text(fmtC(balance > 0 ? balance : 0), pw - MARGIN - 4, y, { align: 'right' });
  y += 10;

  // ── Payment history (compact) ──
  const payments = (invoice.payments || []).filter((p) => !p.voided);
  if (payments.length > 0) {
    y = ensureSpace(y, 14 + payments.length * 4);
    y = drawSectionHeader(doc, y, 'Payment History');

    doc.setFontSize(TYPE.micro);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.muted);
    doc.text('Date', MARGIN + 4, y);
    doc.text('Method', pw / 2, y, { align: 'center' });
    doc.text('Amount', pw - MARGIN - 4, y, { align: 'right' });
    y += 2;
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y, pw - MARGIN, y);
    y += 3;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(TYPE.small);
    payments.forEach((p) => {
      y = ensureSpace(y, 4);
      doc.setTextColor(...BRAND.ink);
      doc.text(fmtD(paymentDate(p)), MARGIN + 4, y);
      doc.setTextColor(...BRAND.muted);
      doc.text(p.method || 'N/A', pw / 2, y, { align: 'center' });
      doc.setTextColor(...BRAND.green);
      doc.text(fmtC(p.amount), pw - MARGIN - 4, y, { align: 'right' });
      y += 4;
    });
    y += 3;
  }

  // ── How to pay (only if there's a balance due) ──
  if (balance > 0 && rawStatus !== 'paid' && rawStatus !== 'void') {
    y = ensureSpace(y, 30);
    y = drawHowToPay(doc, y, { payUrl, cardFeePct, zelleAddress });
  }

  // ── Notes ──
  if (invoice.notes) {
    y = ensureSpace(y, 14);
    y = drawSectionHeader(doc, y, 'Notes');
    doc.setFontSize(TYPE.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    const noteLines = doc.splitTextToSize(invoice.notes, pw - MARGIN * 2 - 8);
    noteLines.forEach((ln) => {
      y = ensureSpace(y, 3.5);
      doc.text(ln, MARGIN + 4, y);
      y += 3.5;
    });
    y += 2;
  }

  // ── Thank-you line ──
  y = ensureSpace(y, 12);
  y += 4;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, pw - MARGIN, y);
  y += 5;
  doc.setFontSize(TYPE.body);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...BRAND.navy);
  doc.text('Thank you for your business.', pw / 2, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');

  // ── Footer on all pages ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, companyName);
  }

  const clientName = (invoice.client || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `Invoice_${invoice.number || 'Draft'}_${clientName}.pdf`;
  if (returnBlob) {
    const blob = doc.output('blob');
    return { blob, filename };
  }
  doc.save(filename);
  return filename;
}
